// The upload queue, as pure functions: what a dropped file becomes, and how its state moves.
//
// A file is checked against the policy the moment it is dropped, so a refused one never costs a
// request; it sits in the list as failed with its reason, and the BD removes it or ignores it.
// Files that pass wait as `queued` until there is a row to attach them to - immediately on an
// existing row, after the first save on a new one - and then go one at a time.

import { validateDocumentFile } from '../../documents/files.js';

export const STATES = Object.freeze({
  queued: 'queued',
  uploading: 'uploading',
  done: 'done',
  failed: 'failed',
});

let sequence = 0;

// `validate` is the policy of what the files are being attached to; the tracker's is the deal
// documents'.
export function enqueue(items, files, validate = validateDocumentFile) {
  const added = Array.from(files).map((file) => {
    const refused = validate(file);
    sequence += 1;
    return {
      key: `${Date.now()}-${sequence}`,
      file,
      state: refused ? STATES.failed : STATES.queued,
      error: refused,
      attachment: null,
    };
  });
  return [...items, ...added];
}

export const update = (items, key, patch) =>
  items.map((item) => (item.key === key ? { ...item, ...patch } : item));

export const remove = (items, key) => items.filter((item) => item.key !== key);

export const queued = (items) => items.filter((item) => item.state === STATES.queued);

export const hasPending = (items) => queued(items).length > 0;

// A failed upload keeps its place so the reason is read; a finished one has been said - the
// row's own list shows it now - and is cleared.
export const withoutDone = (items) => items.filter((item) => item.state !== STATES.done);

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
